import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslator } from '@picsart/rc/services/localization';

import ConfirmDialog from '@/components/ConfirmDialog';
import { ABOVE_LIGHTBOX_Z_INDEX } from '@/consts/lightbox';
import useFileActions from '@/hooks/useFileActions';
import useHideShare from '@/hooks/useHideShare';
import useLightboxPlaybackError from '@/hooks/useLightboxPlaybackError';
import { pushBrowserNav } from '@/hooks/useBrowserHistory';
import { useAppStore, type IAppStore } from '@/store';
import { LightboxSource } from '@/store/lightbox';
import isNativeMobile from '@/utils/platform/isNativeMobile';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import { MediaType } from '@/types';
import type { ContentFile } from '@/types';

const Lightbox = lazy(() => import('@/components/Lightbox'));

const nativeMobile = isNativeMobile();

const selector = (store: IAppStore) => ({
  items: store.lightboxItems,
  index: store.lightboxIndex,
  source: store.lightboxSource,
  resultMode: store.isLightboxResultMode,
  setLightboxIndex: store.setLightboxIndex,
  closeLightbox: store.closeLightbox,
});

/**
 * The app's one lightbox. Self-mounted at the shell and driven by the
 * `lightbox` store slice: callers `openLightbox({ items, index, source })` —
 * one item or many — and this renders it over whatever screen is showing.
 *
 * `source` decides the actions: `Preview` is look-only; `Creations` and
 * `Deeplink` get the full set from `useFileActions`; `HostCreations` drops
 * Reuse template. Handlers stay here, not
 * in the store, so the store only ever holds data.
 */
const AppLightbox = () => {
  const translate = useTranslator();
  const actions = useFileActions();
  const hideShare = useHideShare();
  const isPortfolioApp = checkIsPortfolioApp();
  const handlePlaybackError = useLightboxPlaybackError();
  const { items, index, source, resultMode, setLightboxIndex, closeLightbox } =
    useAppStore(selector);
  const [fileToDelete, setFileToDelete] = useState<ContentFile | null>(null);

  const isOpen = items.length > 0;
  const currentFile = isOpen ? items[index] : undefined;

  // Back closes the lightbox (see `useAppNavigation`'s `onBackFromLightbox`)
  // rather than leaving the screen underneath.
  useEffect(() => {
    if (isOpen) pushBrowserNav('lightbox');
  }, [isOpen]);

  const handlePrev = useCallback(
    () => setLightboxIndex(index - 1),
    [setLightboxIndex, index]
  );
  const handleNext = useCallback(
    () => setLightboxIndex(index + 1),
    [setLightboxIndex, index]
  );
  const handleRunItemSelect = useCallback(
    (file: ContentFile) =>
      setLightboxIndex(items.findIndex((item) => item.uuid === file.uuid)),
    [setLightboxIndex, items]
  );

  const handleShare = useCallback(
    (file: ContentFile) => {
      closeLightbox();
      return actions.handleShare(file);
    },
    [closeLightbox, actions]
  );
  const handleReport = useCallback(
    (file: ContentFile) => {
      closeLightbox();
      return actions.handleReport(file);
    },
    [closeLightbox, actions]
  );
  const handleReuseTemplate = useCallback(
    (file: ContentFile, slug: string) => {
      closeLightbox();
      return actions.handleReuseTemplate(file, slug);
    },
    [closeLightbox, actions]
  );
  const handleConfirmDelete = useCallback(async () => {
    const file = fileToDelete;
    setFileToDelete(null);
    closeLightbox();
    await actions.handleConfirmDelete(file);
  }, [fileToDelete, closeLightbox, actions]);
  const handleCancelDelete = useCallback(() => setFileToDelete(null), []);

  // The opened result's run, for the filmstrip. A deeplink's items are its
  // task's results — the run already; the grid's are the whole list, so the
  // run is picked out by `runId`.
  const runItems = useMemo(() => {
    if (source === LightboxSource.Deeplink) {
      return items.length > 1 ? items : undefined;
    }
    const runId = currentFile?.runId;
    if (!runId) return undefined;
    return items.filter((item) => item.runId === runId);
  }, [source, items, currentFile?.runId]);

  if (!currentFile && !fileToDelete) return null;

  const hasActions = source !== LightboxSource.Preview;
  const canOpenInEditor =
    hasActions &&
    !isPortfolioApp &&
    currentFile?.type !== MediaType.AUDIO &&
    !(nativeMobile && currentFile?.type === MediaType.VIDEO);

  return (
    <>
      {currentFile && (
        <Suspense fallback={null}>
          <Lightbox
            items={items}
            currentIndex={index}
            onClose={closeLightbox}
            onPrev={handlePrev}
            onNext={handleNext}
            resultMode={resultMode}
            onPlaybackError={handlePlaybackError}
            {...(canOpenInEditor && {
              onOpenInEditor: actions.handleOpenInEditor,
            })}
            {...(hasActions && {
              onDownload: actions.handleDownload,
              ...(!hideShare && { onShare: handleShare }),
              onReport: handleReport,
              ...(source !== LightboxSource.HostCreations && {
                onReuseTemplate: handleReuseTemplate,
              }),
              onReact: actions.handleReact,
              onDelete: setFileToDelete,
              runItems,
              onRunItemSelect: handleRunItemSelect,
            })}
          />
        </Suspense>
      )}
      {fileToDelete && (
        <ConfirmDialog
          title={translate('Delete creation?')}
          message={translate(
            'This will permanently remove the file from your creations. This action cannot be undone.'
          )}
          confirmLabel={translate('Delete')}
          cancelLabel={translate('Cancel')}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          zIndex={ABOVE_LIGHTBOX_Z_INDEX}
        />
      )}
    </>
  );
};

export default AppLightbox;
